/// <reference types="@types/google.maps" />
import { inject, Injectable, NgZone } from '@angular/core';
import { AutoCompleteResult } from '@uzurv/rideWss/app/models';
import { isNullOrUndefined } from '@uzurv/rideWss/app/util';
import { Location } from '@uzurv/rideWss/clara';
import { UzurvRestService } from '@uzurv/rideWss/http';
import { from, Observable } from 'rxjs';
import { APP_CONFIGURATION, AppConfiguration } from '../../../../app/app-configuration';
import { map } from 'rxjs/operators';

export interface GeoCoords {
  lng: number;
  lat: number;
  bearing?: number;
}

export interface MapMarker {
  locations: Array<string>;
  color?: string;
  iconUrl?: string;
  anchor?: string;
}

export interface MapRoute {
  polyline: string;
  startNode?: GeoCoords;
  endNode?: GeoCoords;
  distance?: number;
  duration?: number;
  startAddress?: string;
  endAddress?: string;
  type?: string;
  color?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MapsService extends UzurvRestService {
  readonly appConfiguration: AppConfiguration = inject(APP_CONFIGURATION);

  constructor(private zone: NgZone) {
    super();
  }

  /**
   * Returns autocomplete results from the Google Maps Places library
   *
   * @param query
   */
  autocomplete(query: string): Observable<Array<AutoCompleteResult>> {
    return from(
      google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        sessionToken: new google.maps.places.AutocompleteSessionToken(),
        input: query
      })
    ).pipe(map(suggestions => suggestions.suggestions.map(this.convertAutocompleteSuggestionToAutocompleteResult)));
  }

  /**
   * Performs a reverse geocode using the supplied AutoCompleteResult from the Google Maps Geocoder Library
   *
   * @param autoCompleteResult
   */
  geocodeLocationByAutoCompleteResult(autoCompleteResult: AutoCompleteResult): Observable<Location> {
    return from(this.getPlace(autoCompleteResult.locationId)).pipe(map(place => this.convertPlaceToUzurvLocation(place, null)));
  }

  /**
   * Returns a route node marker at the location specified, attached in the map object specified
   *
   * @param options
   */
  buildNodeMarker(options: {
    location: GeoCoords;
    map: google.maps.Map;
    color: string;
    info?: string;
    zIndex?: number;
  }): google.maps.Marker {
    let iconUrl: string;

    switch (options.color) {
      case 'green':
        iconUrl = 'assets/ride-wss-app/map-marker-green.png';
        break;
      case 'red':
        iconUrl = 'assets/ride-wss-app/map-marker-red.png';
        break;
      case 'selected':
        iconUrl = 'assets/ride-wss-app/selected.png';
        break;
      case 'unselected':
        iconUrl = 'assets/ride-wss-app/unselected.png';
        break;
      case 'selected_hovered':
        iconUrl = 'assets/ride-wss-app/selected_hovered.png';
        break;
      case 'unselected_hovered':
        iconUrl = 'assets/ride-wss-app/unselected_hovered.png';
        break;
      case 'new':
        iconUrl = 'assets/ride-wss-app/new.png';
        break;
      default:
        iconUrl = options.color;
        break;
    }

    const marker = new google.maps.Marker({
      position: options.location,
      map: options.map,
      icon: iconUrl,
      zIndex: options.zIndex
    });

    if (!isNullOrUndefined(options.info)) {
      const infoWindow = new google.maps.InfoWindow({
        content: options.info,
        disableAutoPan: true
      });

      marker.addListener('mouseover', () => {
        infoWindow.open(options.map, marker);
      });

      marker.addListener('mouseout', () => {
        infoWindow.close();
      });

      // Setting this as marker data so it can be retrieved and closed
      marker.set('infoWindow', infoWindow);
    }

    marker.addListener('click', () => {
      options.map.setCenter(marker.getPosition());
      options.map.setZoom(15);
    });

    return marker;
  }

  private convertAutocompleteSuggestionToAutocompleteResult(suggestion: google.maps.places.AutocompleteSuggestion): AutoCompleteResult {
    const autoCompleteResult: AutoCompleteResult = new AutoCompleteResult();
    autoCompleteResult.locationId = suggestion.placePrediction.placeId;
    autoCompleteResult.label = suggestion.placePrediction.text.text;
    autoCompleteResult.mainText = suggestion.placePrediction.mainText.text;
    autoCompleteResult.secondaryText = suggestion.placePrediction.secondaryText.text;

    return autoCompleteResult;
  }

  private async getPlace(placeId: string): Promise<google.maps.places.Place> {
    const { place } = await new google.maps.places.Place({ id: placeId }).fetchFields({
      fields: ['addressComponents', 'formattedAddress', 'location']
    });
    return place;
  }

  private convertPlaceToUzurvLocation(place: google.maps.places.Place, autocompleteResult: AutoCompleteResult | null) {
    const location = new Location();

    let streetNumber = this.extractAddressComponentFromPlace(place, 'street_number', 'long');
    const route = this.extractAddressComponentFromPlace(place, 'route', 'long');
    const streetAddress2 = this.extractAddressComponentFromPlace(place, 'room', 'long');
    const locality = this.extractAddressComponentFromPlace(place, 'locality', 'long');
    const stateCode = this.extractAddressComponentFromPlace(place, 'administrative_area_level_1', 'short');
    const zipCode = this.extractAddressComponentFromPlace(place, 'postal_code', 'long');

    if (!isNullOrUndefined(autocompleteResult)) {
      const autoCompleteResultComponents: Array<string> = autocompleteResult.label.split(' ');

      if (isNullOrUndefined(streetNumber)) {
        if (!isNaN(+autoCompleteResultComponents[0])) {
          streetNumber = autoCompleteResultComponents[0];
        }
      }
    }

    location.streetAddress1 = `${streetNumber} ${route}`;
    location.streetAddress2 = streetAddress2;
    location.city = locality;
    location.stateCode = stateCode;
    location.zipCode = zipCode;
    location.display = place.formattedAddress;
    location.latitude = place.location.lat();
    location.longitude = place.location.lng();

    return location;
  }

  private extractAddressComponentFromPlace(
    place: google.maps.places.Place,
    propertyName: string,
    nameType: 'short' | 'long'
  ): string | null {
    const matchingComponent = place.addressComponents.find(it => it.types.includes(propertyName));
    if (isNullOrUndefined(matchingComponent)) {
      return null;
    }

    return nameType === 'long' ? matchingComponent.longText : matchingComponent.shortText;
  }
}
